import { defineStore } from 'pinia';
import { useSiteStore } from './site';
import axios from 'axios';

const usDollarFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// TODO: CHECK EVERY FUNCTION TO SEE IF IT IS BEING USED
export const useCartStore = defineStore({
  id: 'cart',
  state: () => ({
    items: [],
    quantity: 0,
    subtotal: 0,
    total: 0,
    discount: 0,
    bundleUpgrade: false,
    showProrate: false,
    conditions: {},
    shippingZipCode: '',
    removedAndReadded: false,
    discountExceedsTotal: false,
  }),
  getters: {
    itemsGetter() {
      return this.items;
    },
    promoCodes() {
      return Object.keys(this.conditions);
    },
    numberOfItems() {
      return this.items.length;
    },
    shippableItems() {
      return this.items.filter((item) => item.attributes.is_shipped);
    },
    containsInvoiceItems() {
      return this.items.some((item) => item.attributes.type == 'Invoice');
    },
    numberOfShippableItems() {
      return this.shippableItems.length;
    },
    isShippable() {
      return this.numberOfShippableItems > 0;
    },
    numberOfPackages() {
      // return Math.ceil(shippableItems.length / 2)
      // TODO: FIND OUT IF THIS MATH IS CORRECT
      if (!this.numberOfShippableItems) return 0;
      return this.numberOfShippableItems / 2;
    },
    ounceWeightTotal() {
      return this.shippableItems.reduce(
        (acc, item) => acc + item.attributes.ounce_weight,
        0
      );
    },
    poundWeightTotal() {
      return this.shippableItems.reduce(
        (acc, item) => acc + item.attributes.pound_weight,
        0
      );
    },
    ouncesPerPackage() {
      if (!this.ounceWeightTotal || !this.numberOfPackages) return 0;
      return (this.ounceWeightTotal / this.numberOfPackages).toFixed(2);
    },
    poundsPerPackage() {
      if (!this.poundWeightTotal || !this.numberOfPackages) return 0;
      return (this.poundWeightTotal / this.numberOfPackages).toFixed(2);
    },
    totalShippingRate() {
      return this.shippingRate * this.numberOfPackages;
    },
    creditCardFee() {
      return this.subtotal * 0.03;
    },
    formattedSubtotal() {
      return usDollarFormatter.format(this.subtotal);
    },
    formattedTotal() {
      return usDollarFormatter.format(this.total);
    },
    formattedDiscount() {
      return usDollarFormatter.format(this.discount);
    },
    removedAndReaddedGetter() {
      return this.removedAndReadded;
    },
  },
  actions: {
    async fetchCart() {
      try {
        const { data } = await axios.get('/cart/fetch');
        this.items = data.data.items;
        this.quantity = parseInt(data.data.quantity);
        this.subtotal = parseFloat(data.data.subtotal.replace(',', ''));
        this.total = parseFloat(data.data.total.replace(',', ''));
        this.conditions = data.data.conditions;
        this.discount = parseFloat(data.data.discount);
        this.discountExceedsTotal = Boolean(data.data.discount_exceeds_total);
        this.items.forEach((item) => {
          if (item.attributes.bundle_calculator) this.bundleUpgrade = true;
        });
        if (this.items.length === 1 && this.bundleUpgrade)
          this.showProrate = true;
      } catch (error) {
        // TODO: HANDLE ERROR IN A MEANINGFUL WAY
        // TODO: HANDLE ERROR - This is just silencing the error
      }
    },
    async addItem(product) {
      const { data } = await axios.post('/cart', {
        ...product,
      });

      if (data.success !== true) {
        throw new Error('Failed to add item to cart');
      }

      this.fetchCart();
    },
    async removeItem(itemId) {
      try {
        await axios.delete(`/cart/${itemId}`);
        this.fetchCart();
        const siteStore = useSiteStore();
        siteStore.addMessage('Item removed from cart');
      } catch (error) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
    },
    async applyPromo(promoCode) {
      const { data } = await axios.post(`/cart/promo/${promoCode}`);
      if (data.success !== true) {
        throw new Error(data.message);
      }
      this.fetchCart();
    },
    async removePromo(promoCode) {
      const { data } = await axios.delete(`/cart/promo/${promoCode}`, {
        promo_code: promoCode,
      });
      const siteStore = useSiteStore();
      siteStore.addMessage('Code Removed');
      this.fetchCart();
    },
    async addCondition(condition) {
      try {
        await axios.post('/cart/condition', {
          name: cartCondition.name,
          type: cartCondition.type,
          target: cartCondition.target,
          value: cartCondition.value,
        });
        this.fetchCart();
      } catch (error) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
    },
    async updateItemQty(itemId, value) {
      if (value == 0) {
        await this.removeItem(itemId);
        return;
      }

      await axios.post('/cart/updateItemQty', {
        id: itemId,
        qty: value,
      });
      this.fetchCart();
    },
    async clearCartConditions() {
      try {
        await axios.delete(`/cart/conditions`);
        this.fetchCart();
      } catch (error) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
    },
    async clearAllCartConditions() {
      try {
        await axios.post('cart/clear-all-cart-conditions');
        this.fetchCart();
      } catch (error) {
        // TODO: HANDLE ERROR - This is just silencing the error
      }
    },
    async checkCart() {
      for (const item of this.items) {
        if (
          item.attributes.type == 'Subscription' &&
          !item.attributes.bundle_calculator
        ) {
          let data = await axios.get(`/cart/product/${item.id}`);
          if (data.data.changed) {
            this.removedAndReadded = true;
          }
        }
      }
      await this.fetchCart();
      if (this.removedAndReadded) {
        return true;
      }
      return false;
    },
  },
});
